import React from 'react';
import { inject, observer } from 'mobx-react';
import { WithTranslation, withTranslation } from 'react-i18next';
import _isEmpty from 'lodash/isEmpty';
import { logger, Untrusive } from '@lib/common';
import { UI } from '../../../../../../core/ui';
import {
  Modal,
  ModalContent,
  ModalTitle,
  Select,
  FormGroup,
  Button,
  RotateLoader,
  InputGroup,
  InputNumber,
  Input,
} from '@lib/components';
import { MobxComponent } from '../../../../../../mobx/components';

interface State {
  submitting: boolean;
  amountError: boolean;
  nuveiData: {
    amount_captured: number;
    amount_refunded: number;
  }
}

interface Props extends WithTranslation {
  order: T.Schema.Order.OrderSchema;
  restaurant: T.Schema.Restaurant.RestaurantSchema;
  showModal: boolean;
  onClose: () => void;
}

@inject('store')
@observer
class NuveiRefundModalClass extends MobxComponent<Props, State> {
  _amount: any;
  constructor(props: Props) {
    super(props);
    this._amount = React.createRef();
    this.state = {
      submitting: false,
      amountError: false,
      nuveiData: {
        amount_captured: 0,
        amount_refunded: 0
      }
    };
  }

  handleModalClose = () => {
    this.props.onClose();
  };

  handleChange = (value: number): void => {
    console.log("value", value);
    const {
      payment: { currency, total_cents },
    } = this.props.order;
    const divider = currency !== 'JPY' ? 100 : 1;
    const amount = total_cents / divider;

    if (Number(value) > amount || Number(value) < 0) {
      this.setState({ amountError: true });
    } else if (Number(value) + this.state.nuveiData.amount_refunded > amount) {
      this.setState({ amountError: true });
    }
     else {
      this.setState({ amountError: false });
      this._amount.current = Number(value);
    }
  };

  performRefund = async (e: any) => {
    e.preventDefault();

    const { store } = this.injected;
    const amount = this._amount.current;
    const {
      order: { _id: order_id },
      restaurant: { _id: restaurant_id },
    } = this.props;

    try {
      const proceed = confirm('Once refunded, it cannot be reversed. Refund the order?');
      if (!proceed) {
        return;
      }
      Untrusive.start();
      const { _id, payment } = this.props.order;
      const restaurant_id = store.restaurant!._id;
      const response = await store.api.order_nuvei_refund({
        _id,
        restaurant_id,
        amount,
      });
      if (response.outcome) {
        UI.notification.error(response.message, { timeout: 6000 });
      } else {
        store.updateOrderComplete(response.order);
        UI.notification.success('Payment refunded');
      }
    } catch (e) {
      logger.captureException(e);
      UI.notification.error('An error occurred, try again soon or contact us', { timeout: 6000 });
    } finally {
      this.props.onClose();
      Untrusive.stop();
    }
  };

  getOrder =(orderId: string) => {
    const { store} = this.injected;
    return store.api.order_find({_id: orderId})
  }

  async componentDidUpdate(prevProps:any) {
    if (this.props.showModal !== prevProps.showModal) {
      const { _id } = this.props.order;
      if (_id) {
        try {
          const response = await this.getOrder(_id);
          if (response.outcome) {
            UI.notification.error('Cannot get Order Details');
            return;
          }
          this.setState({
             nuveiData: {
              amount_captured: response.item.payment.total,
              amount_refunded: response.item.payment.refunded_amount || 0,
            },
          })
        } catch (error) {
          UI.notification.error(error.message);
          console.log(error.message)
        }
      }
    }
  }

  render() {
    const showModal = this.props.showModal;
    const {t} = this.injected;
    const { submitting, nuveiData} = this.state;
    const {
      payment: { currency, total_cents },
    } = this.props.order;
    const currencyConfig = this.injected.store.intl.s.currency;

    const divider = currency !== 'JPY' ? 100 : 1;
    const amount = total_cents / divider;
    const refundAmount = (nuveiData.amount_captured - nuveiData.amount_refunded);
    const { order } = this.props;
    const refundStatus = order.payment.status;
    let isRefundable: boolean;
    if (typeof(refundStatus) === 'undefined') {
      isRefundable = true;
    } else {
      isRefundable = refundStatus !== 'refunded' || (refundStatus === 'refunded' && nuveiData.amount_captured > nuveiData.amount_refunded);
    }
    const displayRefundAmount = t('currency',{ value : refundAmount })
    return (
      <Modal
        width="xs"
        active={showModal}
        preventClose
        id="nuvei-refund-modal"
        close={this.handleModalClose}
      >
        <ModalTitle className="round-top">
          <h4>Refund</h4>
        </ModalTitle>

        <ModalContent>
        <FormGroup title={`Total Paid`}>
						<Input value={nuveiData.amount_captured} readOnly />
					</FormGroup>
          <FormGroup title={`Refunded`}>
						<Input value={nuveiData.amount_refunded} readOnly />
					</FormGroup>

          <FormGroup
            title={`Refund Amount (Max: ${displayRefundAmount})`}
            error={this.state.amountError ? 'Invalid refund amount' : ''}
          >
            <InputGroup iconHtml={<p className="font-semi-bold">{currency}</p>}>
              <InputNumber
                type="number"
                min={0}
                max={amount}
                required={true}
                step={currencyConfig.step}
                change={(amount: any) => this.handleChange(amount)}
              />
            </InputGroup>
          </FormGroup>

          <FormGroup>
            <Button
              full={true}
              color="primary"
              type="button"
              disabled={submitting || !isRefundable || this.state.amountError}
              onClick={this.performRefund}
            >
              {submitting && <RotateLoader size={2} color="white" />}
              {!submitting && 'Refund'}
            </Button>
          </FormGroup>
        </ModalContent>
      </Modal>
    );
  }
}

export const NuveiRefundModal = withTranslation()(
  NuveiRefundModalClass
);
